<template>
  <el-dialog
    :model-value="isOpen"
    width="744px"
    @opened="opened"
    @closed="closed"
    @close="$emit('close')"
  >
    <el-form
      key="out-of-office-form"
      ref="Form"
      :model="form"
      :rules="formRules"
      label-position="top"
      :disabled="isLoading"
      class="tw-flex tw-flex-col tw-gap-2 tw-mt-3"
      @submit.native.prevent="submitForm"
      @keydown.enter.native.prevent="submitForm"
    >
      <div class="tw-flex tw-items-start tw-gap-2">
        <i-form-item
          prop="start_date"
          :label="$t('common.fields.start_date')"
          :error="errors.start_date?.[0]"
          :class="[
            form.start_date ? 'has-value' : ''
          ]"
        >
          <el-date-picker
            v-model="form.start_date"
            type="date"
            format="dd.MM.yyyy"
            value-format="dd.MM.yyyy"
            class="small"
          />
        </i-form-item>
        <i-form-item
          prop="start_time"
          :label="$t('common.fields.start_time')"
          :error="errors.start_time?.[0]"
          :class="[
            form.start_time ? 'has-value' : ''
          ]"
        >
          <el-time-picker
            v-model="form.start_time"
            class="small"
            value-format="HH:mm"
            format="HH:mm"
          />
        </i-form-item>
      </div>
      <div class="tw-flex tw-items-start tw-gap-2">
        <i-form-item
          prop="end_date"
          :label="$t('common.fields.end_date')"
          :error="errors.end_date?.[0]"
          :class="[
            form.end_date ? 'has-value' : ''
          ]"
        >
          <el-date-picker
            v-model="form.end_date"
            type="date"
            format="dd.MM.yyyy"
            value-format="dd.MM.yyyy"
            class="small"
          />
        </i-form-item>
        <i-form-item
          prop="end_time"
          :label="$t('common.fields.end_time')"
          :error="errors.end_time?.[0]"
          :class="[
            form.end_time ? 'has-value' : ''
          ]"
        >
          <el-time-picker
            v-model="form.end_time"
            class="small"
            value-format="HH:mm"
            format="HH:mm"
          />
        </i-form-item>
      </div>
      <div class="tw-flex tw-items-start tw-gap-2">
        <i-form-item
          prop="out_of_office_replacement_id"
          :label="$t('common.fields.replacement_colleague')"
          :error="errors.out_of_office_replacement_id?.[0]"
        >
          <el-select
            v-model="form.out_of_office_replacement_id"
            placeholder=""
            filterable
            :filter-method="internalUsersFilterMethod"
            @visible-change="handleInternalUsersVisibleChange"
          >
            <el-option
              v-for="item in filteredInternalUsers"
              :key="item.id"
              :label="item.fullname"
              :value="item.id"
            />
          </el-select>
        </i-form-item>
      </div>
    </el-form>
    <template #footer>
      <div class="tw-flex tw-justify-end tw-gap-2 flex-col md:flex-row">
        <i-button
          class="tw-w-40"
          @click="$emit('close')"
        >
          {{ $t('common.actions.cancel') }}
        </i-button>
        <i-button
          type="primary"
          native-type="submit"
          class="tw-w-40"
          :disabled="formIsEmpty || !formHasChanged"
          :is-loading="isLoading"
          @click="submitForm"
        >
          {{ $t('common.actions.save') }}
        </i-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { internalUsersFilterMixin } from '@/mixins/internalUsersFilterMixin';
import { mapGetters, mapState } from 'vuex';
import { cloneDeep } from 'lodash';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

const FORM_BASE = {
  start_date: '', // required
  start_time: '', // required
  end_date: '', //  required
  end_time: '', //  required
  out_of_office_replacement_id: '', // required
};

export default {
  name: 'OutOfOfficeDialog',
  mixins:[internalUsersFilterMixin],
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      form: cloneDeep(FORM_BASE),
      initialForm: {},
      formRules: {
        start_date: [
          { required: true, message: this.$t('common.fields.required'), trigger: 'change' },
        ],
        start_time: [
          { required: true, message: this.$t('common.fields.required'),trigger: 'change'  },
        ],
        end_date: [
          { required: true, message: this.$t('common.fields.required'),trigger: 'change' },
        ],
        end_time: [
          { required: true, message: this.$t('common.fields.required'), trigger: 'change'  },
        ],
        out_of_office_replacement_id: [
          { required: true, message: this.$t('common.fields.required'), trigger: ['change', 'blur']  },
        ],
      },
      errors: {},
    };
  },
  computed: {
    ...mapState({
      me: (state) => state.v2.shared.profile.me,
      outOfOfficeProfile: (state) => state.v2.shared.profile.me.profile,
    }),
    ...mapGetters({
      internalUserOptions: 'v2/admin/cms/internal/users/selectOptions',
    }),
    internalUsers() {
      return [{ fullname: ' ', id: null  }, ...this.internalUserOptions].filter((internalUser) => internalUser.id !== this.me.id);
    },
    formIsEmpty() {
      return Object.keys(this.form).every((key) => {
        if (this.form[key] === null || this.form[key] === undefined) {
          return true;
        }
        return this.form[key].toString().trim() === '';
      });
    },
    formHasChanged() {
      return !_isEqual(this.form, this.initialForm);
    },
  },

  watch: {
    outOfOfficeProfile() {
      this.setInitialFormData();
    },
    async isOpen(val) {
      this.$refs.Form?.clearValidate();
    },
  },
  mounted() {
    this.$store.dispatch('v2/admin/cms/internal/users/loadAllUsers');
  },
  methods: {
    opened() {
      this.$refs.Form?.clearValidate();
    },
    closed() {
      this.$refs.Form?.clearValidate();
    },
    setInitialFormData() {
      const outOfOfficeKeys = Object.keys(this.outOfOfficeProfile);
      const formKeys = Object.keys(this.form);

      formKeys.forEach((key) => {
        if (outOfOfficeKeys.includes(key)) {
          this.form[key] = this.outOfOfficeProfile[key];
          this.initialForm[key] = this.outOfOfficeProfile[key];
        }
      });
      this.$refs.Form?.clearValidate();
    },
    async submitForm() {
      this.errors = {};
      const isValid = await this.$refs.Form.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;

      try {
        const response = await this.$store.dispatch('v2/shared/profile/updateOutOfOffice', {
          startDate: this.form.start_date,
          startTime: this.form.start_time,
          endDate: this.form.end_date,
          endTime: this.form.end_time,
          replacementUserId: this.form.out_of_office_replacement_id,
        });

        const message = _get(response, 'data.message');
        this.$message.success({
          message: this.$capitalize(message || 'Success'),
          showClose: true,
        });

        this.$emit('refresh');
        this.$emit('close');
      } catch (error) {
        const errorText = error.response.data.data?.internal_profile?.non_field_errors?.[0] ?? error.response.data.message;
        this.errors = error.response.data.data?.internal_profile;

        this.$message.error({
          message: errorText,
          showClose: true,
        });
      }

      this.isLoading = false;
    },
  },
};
</script>
